import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";
import { HeartPulse, Bone, Ribbon, Flower2, ArrowRight } from "lucide-react";

const ICONS = {
    heart: HeartPulse,
    bone: Bone,
    ribbon: Ribbon,
    flower: Flower2,
};

export const Specialties = () => {
    const { t, site: siteData } = useLang();
    const { SPECIALTIES } = siteData;
    usePageMeta(
        t("meta.specialties.title", "Specialties"),
        t("meta.specialties.desc", "UniversHeal coordinates care in Cardiology, Orthopaedics, Oncology and Fertility — our launch specialties for international patients in Kerala, India.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("specialties.eyebrow", "Specialties")}
                title={t("specialties.title", "Specialized care. Personal attention.")}
                intro={t("specialties.intro", "Our initial focus areas at launch. These are launch categories — specialist availability is confirmed case by case, based on your needs. Each specialty has its own page with the conditions commonly supported and how we help.")}
            />

            <section className="pb-10">
                <div className="container-site grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {SPECIALTIES.map((spec, i) => {
                        const Icon = ICONS[spec.icon];
                        return (
                            <Reveal key={spec.slug} delay={i * 0.07} className="h-full">
                                <Link
                                    to={`/specialties/${spec.slug}`}
                                    data-testid={`specialty-card-${spec.slug}`}
                                    className="group h-full flex flex-col bg-white border border-pine/10 rounded-2xl overflow-hidden card-lift"
                                >
                                    <span className="relative block h-48 overflow-hidden">
                                        <img
                                            src={IMG[spec.img]}
                                            alt={spec.imgAlt}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            loading="lazy"
                                        />
                                        <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest2 text-pine bg-ivory/95 px-2.5 py-1.5 rounded-md border border-pine/10">
                                            {t("specialties.launchChip", "Launch category")}
                                        </span>
                                    </span>
                                    <span className="flex flex-col flex-1 p-7">
                                        <span className="flex items-center gap-3">
                                            <Icon
                                                className="w-5 h-5 text-forest shrink-0"
                                                strokeWidth={1.5}
                                            />
                                            <span className="font-serif font-medium text-[1.55rem] leading-tight text-forest">
                                                {spec.name}
                                            </span>
                                        </span>
                                        <span className="block mt-2 font-serif italic text-lg text-pine/80">
                                            {spec.tagline}
                                        </span>
                                        <span className="block mt-2 text-sm leading-relaxed text-moss flex-1">
                                            {spec.desc}
                                        </span>
                                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-forest">
                                            {t("specialties.viewCta", "View specialty")}
                                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                        </span>
                                    </span>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <section className="section-pad pt-0">
                <div className="container-site max-w-4xl">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-64 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.hero}
                                alt="An experienced Indian specialist warmly consulting an international patient in a modern Kerala clinic"
                                data-testid="specialties-index-image"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("specialties.ctaHeadline", "Don't see your concern listed?")}
                text={t("specialties.ctaText", "These are our launch categories — tell us about your needs anyway, and we will help you understand how we can coordinate appropriate care.")}
            />
        </>
    );
};
